import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminUser } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Input } from "@/ui/Field";
import { Chip } from "@/ui/Chip";
import { ACCESS_LABEL, ACCESS_TONE } from "@/features/admin/packages";
import { ExportButton } from "@/features/admin/TableActions";
import { useFilters } from "@/features/admin/useFilters";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, shortTime } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";

export const Route = createFileRoute("/_app/admin/users/")({
  component: PlatformPeopleScreen,
});

/**
 * Finding a person.
 *
 * A support conversation starts from an address — "I cannot sign in" arrives
 * before anybody knows which organisation it is about. Before this screen the
 * only way to answer it was to guess the tenant and read the account from
 * there, which meant opening a customer's organisation to answer a question
 * about a person.
 */
function PlatformPeopleScreen() {
  const filters = useFilters();
  const search = filters.get("q");
  const setSearch = (value: string) => filters.set("q", value);
  const debounced = useDebounced(search, 250);

  const users = useQuery({
    queryKey: ["platform-users", debounced],
    queryFn: ({ signal }) =>
      api.get<{ users: AdminUser[] | null }>(
        "/admin/users",
        { search: debounced || undefined, limit: 100 },
        signal,
      ),
  });

  const list = users.data?.users ?? [];

  return (
    <Panel
      /* The search leads. With only an export on the right the band was one
         button against the edge and a thousand empty pixels; a search box is
         the control somebody reaches for first and it belongs where the eye
         starts. */
      toolbar={
        <Input
          value={search}
          placeholder="Address or name"
          aria-label="Search people"
          className="h-8 w-72 text-[13px]"
          onChange={(e) => setSearch(e.target.value)}
        />
      }
      actions={
        <div className="flex items-center gap-2">
          <ExportButton
            name="drumlin-people"
            rows={list}
            columns={[
              { header: "email", value: (u) => u.email },
              { header: "name", value: (u) => u.display_name ?? "" },
              { header: "status", value: (u) => u.status },
              { header: "access", value: (u) => u.access },
              { header: "email_verified", value: (u) => String(u.email_verified) },
              { header: "mfa", value: (u) => String(u.mfa_enabled) },
              { header: "staff", value: (u) => String(u.platform_admin) },
              { header: "organisations", value: (u) => u.organisations },
              { header: "last_seen", value: (u) => u.last_seen_at ?? "" },
              { header: "joined", value: (u) => u.created_at },
            ]}
          />
        </div>
      }
      padded={false}
    >
      {users.isLoading ? (
        <div className="px-5 py-4">
          <TableSkeleton rows={6} columns={5} />
        </div>
      ) : users.isError ? (
        <div className="px-5 py-4">
          <ErrorState error={users.error} onRetry={() => void users.refetch()} />
        </div>
      ) : list.length === 0 ? (
        <div className="px-5 py-8">
          <EmptyState
            title={search ? "Nobody matches that" : "No accounts"}
            description={
              search ? "Try part of an address." : "Nobody has registered on this installation."
            }
          />
        </div>
      ) : (
        <div className="px-5 py-3">
          <Table dense fixed>
            <thead>
              <Tr>
                <Th width="w-[28%]">Address</Th>
                <Th width="w-[24%]">Standing</Th>
                <Th width="w-[10%]" numeric>
                  Orgs
                </Th>
                <Th width="w-[10%]" numeric>
                  Sessions
                </Th>
                <Th width="w-[14%]" align="right">
                  Last seen
                </Th>
                <Th width="w-[14%]" align="right">
                  Joined
                </Th>
              </Tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <Tr key={u.id}>
                  {/* An address is the value that was making these rows
                      different heights from every other table's. It clips and
                      keeps the whole thing on hover. */}
                  <Td clip title={u.display_name ? `${u.email} — ${u.display_name}` : u.email}>
                    <Link
                      to="/admin/users/$userId"
                      params={{ userId: u.id }}
                      className="font-medium text-[var(--text-primary)] hover:text-[var(--primary)]"
                    >
                      {u.email}
                    </Link>
                  </Td>
                  <Td clip title={u.suspension_reason || undefined}>
                    <span className="flex items-center gap-1">
                      {/* Suspended first and alone-looking, because it is the
                          one badge here that changes what the person can do. */}
                      {u.status === "suspended" && <Chip tone="down">Suspended</Chip>}
                      {u.platform_admin && <Chip tone="live">Staff</Chip>}
                      {u.access !== "approved" && (
                        <Chip tone={ACCESS_TONE[u.access] ?? undefined}>
                          {ACCESS_LABEL[u.access] ?? u.access}
                        </Chip>
                      )}
                      {!u.email_verified && <Chip tone="warn">Unconfirmed</Chip>}
                      {u.mfa_enabled && <Chip tone="up">2FA</Chip>}
                    </span>
                  </Td>
                  <Td numeric>{u.organisations}</Td>
                  <Td numeric>{u.sessions}</Td>
                  <Td align="right" title={u.last_seen_at ? absoluteTime(u.last_seen_at) : undefined}>
                    {u.last_seen_at ? shortTime(u.last_seen_at) : "—"}
                  </Td>
                  <Td align="right" title={absoluteTime(u.created_at)}>
                    {shortTime(u.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Panel>
  );
}
